import { queryOptions } from '@tanstack/react-query'

import type { OtpRequestListResponse } from '@/bindings/OtpRequestListResponse'
import type { OtpRequestSummary } from '@/bindings/OtpRequestSummary'
import { request } from '@/lib/api'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { type PageSearch, parsePageSearch } from '@/lib/page-search'
import { PAGE_SIZE } from '@/lib/paging'

export type OtpStatus = 'pending' | 'sent'

export interface OtpSearch extends PageSearch {
  status: OtpStatus
}

/** `?status=sent` shows the history; anything else is the pending queue. */
export function parseOtpSearch(search: Record<string, unknown>): OtpSearch {
  return { ...parsePageSearch(search), status: search.status === 'sent' ? 'sent' : 'pending' }
}

/** `all` prefixes both views, so one invalidation after a generate refreshes them together. */
export const otpRequestKeys = {
  all: ['platform', 'otp-requests'] as const,
  list: (status: OtpStatus, page: number) => ['platform', 'otp-requests', status, page] as const,
}

export function otpRequestListQuery(status: OtpStatus, page: number) {
  return queryOptions({
    queryKey: otpRequestKeys.list(status, page),
    queryFn: () =>
      request<OtpRequestListResponse>(`/platform/otp-requests?status=${status}&page=${page}&per_page=${PAGE_SIZE}`),
  })
}

export function generatePath(id: string) {
  return `/platform/otp-requests/${id}/generate`
}

export const PURPOSE_LABELS: Record<OtpRequestSummary['purpose'], MessageKey> = {
  verify_phone: 'otp.purpose.verify_phone',
  reset_password: 'otp.purpose.reset_password',
}

export type SentState = 'used' | 'expired' | 'active'

/** What became of a generated code: redeemed, timed out, or still redeemable. */
export function sentState(item: OtpRequestSummary, now = Date.now()): SentState {
  if (item.consumed_at) return 'used'
  if (item.expires_at && new Date(item.expires_at).getTime() < now) return 'expired'
  return 'active'
}
